import React, { FC } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { DatetimeFormatType, DatetimeMonthFormat, TypeOfDate } from 'app/lib/datetime/types';
import type { ReduxState } from 'app/redux/types';

import Datetime from 'app/design-system/components/Datetime';
import { Expander } from 'app/design-system/components/Expander';
import { useTranslation } from 'app/lang';
import { SlotType } from 'app/lib/betting/betting-entities';
import { replacingHyphenOnDash } from 'app/lib/replaceDashSymbol';
import { StakeType } from 'app/lib/staking/constants';
import LiveBadge, { BadgePreset } from 'app/widgets/LiveBadge';

import { AccountType, AmountValidationResult, SubmissionState } from '../../../constants';
import { selectHasEnoughMoney } from '../../../selectors';
import { selectIsConflictedEntry } from '../../../selectors/conflict';
import {
  selectBets,
  selectLockedBetErrorMessage,
  selectMatches,
  selectRequestErrorShowing,
  selectStakes,
  selectTotalAmountActive,
} from '../../../selectors/entries';
import { selectCurrentStakeType } from '../../../selectors/stakeTypes';
import {
  selectBetInfoSubmissionByBetId,
  selectEntryAmountValidation,
  selectIsOnlyAcceptAmountSingleError,
  selectSingleBetsInfo,
} from '../../../selectors/stakeTypes/single';
import { selectConflictState } from '../../../selectors/state';
import AmountInput from '../../EntryComponents/AmountInput';
import EntryHeader from '../../EntryComponents/Header';
import Name from '../../EntryComponents/Name';
import PriceDiff from '../../PriceDiff';
import Submission from '../../Submission';
import Error from './Error';
import Footer from './Footer';

import dictionary from './Entry.dictionary.json';

import entryStyles from '../../EntryComponents/EntryComponents.module.scss';
import styles from './Entry.module.scss';

// ---

type StoreProps = {
  entityPrice: number;
  entryPrice: number;
  stakeName?: string;
  matchName?: string;
  matchStartDate?: Date;
  showFooter: boolean;
  showRequestError: boolean;
  hasInputError: boolean;
  isLockedMessage: boolean;
  hasConflict: boolean;
  isConflictedEntry: boolean;
  isLive: boolean;
  isSingle: boolean;
  disabled: boolean;
  totalAmountActive: boolean;
  isAwaitingBet: boolean;
  isOnlyAcceptSingleError: boolean;
};

type EntryProps = {
  betId: number;
};

const Entry: FC<EntryProps & StoreProps> = ({
  betId,
  entryPrice,
  entityPrice,
  stakeName,
  matchName,
  matchStartDate,
  showFooter,
  showRequestError,
  hasInputError,
  isLockedMessage,
  hasConflict,
  isConflictedEntry,
  isLive,
  isSingle,
  disabled,
  totalAmountActive,
  isAwaitingBet,
  isOnlyAcceptSingleError,
}) => {
  const { t } = useTranslation();
  const _className = classNames(styles.container, {
    [styles.disabledEntry]: !isSingle && disabled,
    [styles.notConflictedEntry]: hasConflict && !isConflictedEntry,
  });

  const replacedMatchName = replacingHyphenOnDash(matchName);

  const submission  = (
    <Submission betId={betId}
                data-test="entry-submission" />
  );

  return (
    <div className={_className}
         data-scroll={betId}
         data-test="entry-container">
      <EntryHeader betId={betId}
                   data-test="entry-header" />

      <main className={styles.main}
            data-test="entry-main">
        <div className={styles.stakeNameContainer}
             data-test="entry-stakeNameContainer">
          <Name value={stakeName}
                data-test="entry-stakeName"
                className={entryStyles.stakeName}
                error={showRequestError || isLockedMessage} />

          {!showRequestError && !isLockedMessage && (
            <PriceDiff data-test="entry-priceDiff"
                       prev={entryPrice}
                       next={disabled ? entryPrice : entityPrice} />
          )}
        </div>

        <Name value={replacedMatchName}
              data-test="entry-matchName"
              className={entryStyles.matchName}
              error={showRequestError || isLockedMessage} />

        {!showRequestError && !isLockedMessage && !isConflictedEntry && (
          <div data-test="entry-badges"
               className={classNames(styles.badges, {
                 [styles.singleBadges]: isSingle,
               })}>
            {isLive && (
              <LiveBadge preset={BadgePreset.BET_LIVE} />
            )}
            <Datetime value={matchStartDate}
                      data-test="entry-datetime"
                      title={t(dictionary.datetimeTitle)}
                      className={entryStyles.datetime}
                      format={DatetimeFormatType.LONG}
                      typeOfDate={TypeOfDate.MATCH_DATE}
                      monthFormat={DatetimeMonthFormat.SHORT} />
          </div>

        )}
      </main>

      <Error betId={betId} />

      {showFooter && !hasConflict && !showRequestError && !isLockedMessage && (
        <footer className={styles.footerWrapper}
                data-test="entry-footer">
          {isAwaitingBet ? (
            <>
              <AmountInput betId={betId}
                           hasError={(!disabled && hasInputError) || isOnlyAcceptSingleError} />

              <Footer betId={betId} />
            </>
          ) : submission}
        </footer>
      )}

      {totalAmountActive && (
        <Expander isOpen={!isAwaitingBet}
                  duration={250}>
          {submission}
        </Expander>
      )}

    </div>
  );
};

const mapStateToProps = (state: ReduxState, { betId }: EntryProps): StoreProps => {
  const entries = selectBets(state);
  const stakes = selectStakes(state);
  const matches = selectMatches(state);
  const betInfo = selectSingleBetsInfo(state).get(betId);

  const isSingle = selectCurrentStakeType(state).has(StakeType.SINGLE);

  const entry = entries.get(betId);
  const stake = entry && stakes.get(entry.entity.stakeId);
  const match = entry && matches.get(entry.entity.matchId);

  const singleAmount = betInfo?.get('amount');
  const amount = (singleAmount?.selector) ? singleAmount.selector(state) : singleAmount;

  const validation = selectEntryAmountValidation(state, betId);
  const showMinLimitError = isSingle && validation === AmountValidationResult.MIN_NOT_REACHED;
  const showMaxLimitError = validation === AmountValidationResult.MAX_EXCEEDED;

  const hasInputError = (
    (!selectHasEnoughMoney(state) && amount?.account === AccountType.MAIN_ACCOUNT)
    || showMinLimitError
    || showMaxLimitError
  );

  const disabled = selectBetInfoSubmissionByBetId(state, betId) !== SubmissionState.AWAITING;

  const props = {
    isOnlyAcceptSingleError: selectIsOnlyAcceptAmountSingleError(state, betId),
    entryPrice: entry?.price || 0,
    entityPrice: entry?.price || 0,
    stakeName: stake?.entity.names.get('long'),
    matchName: match?.entity.name,
    matchStartDate: match?.entity.startDate,
    showFooter: !selectTotalAmountActive(state) && isSingle,
    showRequestError: selectRequestErrorShowing(state, betId),
    isLockedMessage: !!selectLockedBetErrorMessage(state, betId) && !disabled,
    hasConflict: selectConflictState(state) !== null,
    isConflictedEntry: selectIsConflictedEntry(state, betId),
    isAwaitingBet: selectBetInfoSubmissionByBetId(state, betId) === SubmissionState.AWAITING,
    totalAmountActive: selectTotalAmountActive(state),
    isLive: match?.entity.slotType === SlotType.LIVE,
    disabled,
    hasInputError,
    isSingle,
  };

  if (betInfo && entry && betInfo.submission !== SubmissionState.PROCESSING) {
    props.entityPrice = entry.entity.price || 0;
  }

  return props;
};

export default connect(mapStateToProps)(Entry);
